import { useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Table,
  IconExpand,
  SchemaFilterType,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector } from '@store';
import { columnsItems, columnsLabels } from './variables/list';
import formatExternalList from './helpers/format-external-list';
import useListFunctions from './hooks/use-list';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const ExternalAccountsList = () => {
  const [takeCount, setTakeCount] = useState(0);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    externalAccounts,
    pagination,
    pspPartners,
    casp,
    externalAccountsErrorsList,
  } = useAppSelector((state) => state.externalAccounts);
  const { activePermissions, paymentTypes, currenciesList } = useAppSelector(
    (state) => state.root,
  );

  const { search } = useLocation();

  const {
    handleSubmit,
    isLoading,
    setPage,
    errorList,
    filtersComponent,
    filters,
    onFilter,
    isFetching,
  } = useListFunctions({
    scrollRef,
    pspPartners,
    externalAccounts,
    search,
    takeCount,
    setTakeCount,
  });

  const formattedData = useMemo(() => {
    const data = formatExternalList(
      externalAccounts,
      paymentTypes,
      casp?.map((item) => ({ name: item.name, value: item.id })),
      currenciesList,
    );

    return data;
  }, [externalAccounts, paymentTypes, filters, casp, currenciesList]);

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <div className={cn.externalTop}>
          {activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
          ) && (
            <div className={cn.externalTopCreateLinkWrapper}>
              <NavLink
                to={`${ROUTES.EXTERNAL_ACCOUNTS_LIST_CREATE}${search || ''}`}
                className={cn.externalTopCreateLink}
              >
                <IconExpand width={16} height={16} /> Add external account
              </NavLink>
            </div>
          )}
        </div>
        <div className={cn.externalAccountsTableWrapper}>
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              onFilter({
                ...values,
                take: {
                  value: [takeCount.toString()],
                  type: SchemaFilterType.STRING,
                },
              });
            }}
            error={externalAccountsErrorsList || errorList}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching || takeCount === 0}
            storeKey="external-accounts"
            pagination={pagination}
            tableBodyClassName={isLoading || isFetching ? cn.tableLoading : ''}
            tableFormClassName={cn.externalAccountsTable}
            take={takeCount.toString()}
            setPage={(data) => {
              setPage({
                ...data,
                filters: {
                  ...filters,
                  take: {
                    value: [takeCount.toString()],
                    type: SchemaFilterType.STRING,
                  },
                },
              });
            }}
            withScroll
            clickable
          />
        </div>

        <Outlet />
      </>
    );
  }

  return (
    <div className={cn.externalBottom} ref={scrollRef}>
      {content}
    </div>
  );
};

export default ExternalAccountsList;
